import { useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { NotificationsProvider } from "@/context/NotificationsContext";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { CompanyProvider } from "@/context/CompanyContext";
import { FullScreenLoader } from "@/components/FullScreenLoader";
import { SidebarContent } from "@/components/layout/Sidebar";
import { TopBar } from "@/components/layout/TopBar";
import { BottomNav } from "@/components/layout/BottomNav";
import { ForcePasswordChange } from "@/components/ForcePasswordChange";

const SECTION_PATHS = { dashboard: ["/dashboard"], sales: ["/sales"], outstanding: ["/outstanding"], expenses: ["/expenses", "/lines/"], cogs: ["/cogs"], reports: ["/reports"], monthly: ["/reports"] };

export const hasApprovals = (u) => u.is_super_admin || u.role === "company_admin" || ["submit", "recommend", "approve"].some((k) => u.authority?.[k]);

/** Task users (MD/DH/Manager/Employee) only reach Tasks, Profile and the sections granted to them. */
export const taskUserAllowed = (user, path) =>
  path.startsWith("/tasks") || path === "/work" || path === "/profile" || path === "/documents" || path === "/notifications" || (path.startsWith("/approvals") && hasApprovals(user)) || user.sections.some((s) => (SECTION_PATHS[s] || []).some((p) => path.startsWith(p)));

export default function ProtectedLayout() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (user === null) return <FullScreenLoader />;
  if (!user) return <Navigate to={localStorage.getItem("pnl_login_slug") ? `/login/${localStorage.getItem("pnl_login_slug")}` : "/login"} replace />;
  if (user.is_task_user && !taskUserAllowed(user, pathname)) return <Navigate to="/work" replace />;

  return (
    <CompanyProvider>
      <NotificationsProvider>
      <div className="min-h-screen bg-slate-50" data-testid="app-shell">
        <TopBar onMenu={() => setMobileOpen(true)} />
        <div className="flex">
        <aside className="sticky top-[var(--topbar-h,4rem)] hidden h-[calc(100dvh-var(--topbar-h,4rem))] w-60 shrink-0 bg-slate-900 lg:block" data-testid="sidebar">
          <SidebarContent />
        </aside>
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="right" className="pt-safe pb-safe pr-[env(safe-area-inset-right)] w-[min(18rem,85vw)] border-0 bg-slate-900 p-0 text-slate-100">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SidebarContent onNavigate={() => setMobileOpen(false)} mobile />
          </SheetContent>
        </Sheet>
        <div className="flex min-w-0 flex-1 flex-col">
          <main className={`pl-safe pr-safe min-w-0 flex-1 overflow-x-clip px-3 py-4 sm:px-8 sm:py-5 lg:px-10 lg:py-8 ${user.is_task_user ? "pb-24 lg:pb-8" : "pb-safe"}`}>
            <Outlet />
            {user.must_change_password && !user.impersonated_by && <ForcePasswordChange />}
          </main>
        </div>
        </div>
        {user.is_task_user && <BottomNav />}
      </div>
      </NotificationsProvider>
    </CompanyProvider>
  );
}
